import { useAuthenticated } from '@jotta/auth-client/useAuthStatus'
import {
  getCustomerQueryKey,
  useGetCustomer,
} from '@jotta/grpc-connect-client/customer'
import { SubscriptionType } from '@jotta/grpc-connect-openapi/customer'
import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@connectrpc/connect-query'
import { getOffer } from '@jotta/grpc-connect-openapi/offerQuery'
import { Country } from '@jotta/grpc-connect-openapi/country'
import {
  authTransport,
  publicTransport,
} from '@jotta/grpc-connect-client/transport'
import type { UserType } from '../auth/CreateUser'
import { getSupportedPaymentMethods as getSupportedPaymentMethodsDescriptor } from '@jotta/grpc-connect-openapi/subscriptionQuery'
import type { Offer } from '@jotta/grpc-connect-openapi/offer'
import { Offer_ProductScope } from '@jotta/grpc-connect-openapi/offer'
import type { GetSupportedPaymentMethodsResponse_PaymentMethod } from '@jotta/grpc-connect-openapi/payment'
import { patchCustomer as patchCustomerDescriptor } from '@jotta/grpc-connect-openapi/customerQuery'
import { useCurrentLanguage } from '@jotta/i18n'
import { queryClient } from '@jotta/query'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import { getCurrentCountry } from '@jotta/grpc-connect-openapi/geoQuery'
import { useBrandingInfo } from '@jotta/grpc-connect-client/config'
import { usePaymentInfo } from './usePaymentInformation'
import type {
  GetPaymentInfoResponse,
  RecurringSubscriptionInterval,
} from '@jotta/grpc-connect-openapi/payment'
import { createConnectQuery } from '@jotta/grpc-connect-client/createConnectQuery'

export type OfferState = {
  productCode: string | undefined
  type: UserType | undefined
  free: boolean
  authenticated: boolean
  offer: Offer | undefined
  paymentMethods: GetSupportedPaymentMethodsResponse_PaymentMethod[] | undefined
  currentPaymentInfo: GetPaymentInfoResponse | undefined
  interval: RecurringSubscriptionInterval
  setInterval: (interval: RecurringSubscriptionInterval) => void
  country: Country
  countryCode: string
  setCountryCode: (country: string) => void
  extraStorageTB: number
  setExtraStorageTB: (extraStorageTB: number) => void
  minimumExtraStorage: number
  extraSeats: number
  setExtraSeats: (extraSeats: number) => void
  voucherCode: string
  setVoucherCode: (voucherCode: string) => void
  subscriptionType: SubscriptionType
  isRefetching: boolean
  isValid: boolean
  isPending: boolean
}

function toUserType(productScope: Offer_ProductScope): UserType {
  switch (productScope) {
    case Offer_ProductScope.PERSONAL:
    case Offer_ProductScope.HOME:
      return 'private'
    case Offer_ProductScope.BUSINESS:
      return 'business'
    case Offer_ProductScope.UNKNOWN_SCOPE:
      throw new Error()
  }
}

function isUnrecoverableInvalidOffer(offer: Offer) {
  const status = offer.status
  switch (status.case) {
    case 'valid':
    case 'recommended':
    case 'currentSubscription':
      return false
    case undefined:
    case 'invalid':
    case 'storageExceeded':
    case 'usersExceeded':
    case 'businessUserNotPermitted':
      return true
    default:
      exhaustiveGuard(status)
  }
}

function isValidVoucherState(
  voucherCodeStatus: Offer['voucherCodeStatus'] | undefined,
  interval: RecurringSubscriptionInterval,
) {
  switch (voucherCodeStatus?.case) {
    case undefined:
    case 'couponPossible':
      return true
    case 'validCoupon':
      return voucherCodeStatus.value.validForIntervals.includes(interval)
    case 'validVoucher':
    case 'invalidCode':
      return false
    default:
      exhaustiveGuard(voucherCodeStatus)
  }
}

const getOfferQuery = createConnectQuery(getOffer)

export function useOfferState(
  productCode: string | undefined,
  defaultSubscriptionInterval: RecurringSubscriptionInterval,
  {
    initialExtraStorageTB = 0,
    initialExtraSeats = 0,
  }: { initialExtraStorageTB?: number; initialExtraSeats?: number } = {},
): OfferState {
  const { customerGroupCode, defaultCountry } = useBrandingInfo()
  const authenticated = useAuthenticated() === 'TRUE'
  const customer = useGetCustomer().data
  const subscriptionType =
    customer?.subscriptionType || SubscriptionType.UNKNOWN_SUBSCRIPTION_TYPE

  const language = useCurrentLanguage()
  const { data: currentCountry } = useQuery(
    getCurrentCountry,
    {},
    {
      throwOnError: false,
      select: data => data.country,
    },
  )

  const countryCode = customer?.countryCode.toLowerCase() || ''
  const [extraStorageTB, _setExtraStorageTB] = useState(initialExtraStorageTB)
  const [extraSeats, _setExtraSeats] = useState(initialExtraSeats)
  const [voucherCode, setVoucherCode] = useState('')

  const [interval, setInterval] = useState<RecurringSubscriptionInterval>(
    defaultSubscriptionInterval,
  )

  const { mutate: patchCustomer } = useMutation(patchCustomerDescriptor, {
    transport: authTransport,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getCustomerQueryKey,
      })
    },
  })

  const country =
    customer?.country || currentCountry || defaultCountry || Country.NORWAY

  const getOfferParams = {
    language,
    productCode,
    extraStorageTb: extraStorageTB,
    extraSeats,
    voucherCode,
    ...(authenticated
      ? {}
      : {
          unauthenticatedParams: {
            customerGroupCode,
            country,
          },
        }),
  }

  const {
    data: getOfferResponse,
    refetch: refetchOffer,
    isRefetching,
    isPending,
  } = getOfferQuery({
    variables: getOfferParams,
    enabled: Boolean(productCode),
    transport: authenticated ? authTransport : publicTransport,
    placeholderData: previousData => previousData,
  })

  const currentPaymentInfo = usePaymentInfo({
    enabled: authenticated,
  }).data

  const {
    data: getSupportedPaymentMethodsResponse,
    refetch: refetchPaymentMethods,
  } = useQuery(
    getSupportedPaymentMethodsDescriptor,
    {},
    {
      enabled: authenticated,
      transport: authTransport,
    },
  )

  const setCountryCode = useCallback(
    (countryCode: string) => {
      patchCustomer(
        {
          countryCode: {
            value: countryCode,
          },
        },
        {
          onSuccess: () => {
            refetchOffer()
            refetchPaymentMethods()
          },
        },
      )
    },
    [country, patchCustomer, refetchOffer, refetchPaymentMethods],
  )
  const offer = getOfferResponse?.offer
  const productScope = offer?.productScope
  const type = productScope ? toUserType(productScope) : undefined
  const free = !offer?.yearly?.basePrice?.amount
  const minimumExtraStorageTB = offer?.minimumExtraStorageTb ?? 0
  const minimumExtraSeats = offer?.minimumExtraSeats ?? 0

  const setExtraStorageTB = useCallback(
    (v: number) => _setExtraStorageTB(Math.max(minimumExtraStorageTB, v)),
    [_setExtraStorageTB, minimumExtraStorageTB],
  )

  const setExtraSeats = useCallback(
    (v: number) => _setExtraSeats(Math.max(minimumExtraSeats, v)),
    [_setExtraSeats, minimumExtraSeats],
  )

  if (offer && isUnrecoverableInvalidOffer(offer)) {
    throw new Error('invalid offer')
  }

  const isValid = isValidVoucherState(offer?.voucherCodeStatus, interval)

  useEffect(() => {
    if (extraStorageTB < minimumExtraStorageTB) {
      getOfferQuery.reset(getOfferParams) // prevent displaying stale data while the offer is re-fetched
      _setExtraStorageTB(minimumExtraStorageTB)
    }
  }, [minimumExtraStorageTB, extraStorageTB, _setExtraStorageTB])

  useEffect(() => {
    if (extraSeats < minimumExtraSeats) {
      getOfferQuery.reset(getOfferParams) // prevent displaying stale data while the offer is re-fetched
      _setExtraSeats(minimumExtraSeats)
    }
  }, [minimumExtraSeats, extraSeats, _setExtraSeats])

  return {
    productCode,
    type,
    authenticated,
    free,
    extraStorageTB: offer?.extraStorageAvailable
      ? Math.max(minimumExtraStorageTB, extraStorageTB)
      : 0,
    setExtraStorageTB,
    minimumExtraStorage: minimumExtraStorageTB,
    extraSeats: offer?.extraSeatsAvailable
      ? Math.max(minimumExtraSeats, extraSeats)
      : 0,
    setExtraSeats,
    offer: offer,
    paymentMethods: getSupportedPaymentMethodsResponse?.paymentMethods,
    currentPaymentInfo,
    interval: interval,
    setInterval: setInterval,
    country,
    countryCode,
    setCountryCode,
    voucherCode,
    setVoucherCode,
    subscriptionType,
    isRefetching,
    isValid,
    isPending,
  }
}
